import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BookCard, BookForm, FilterPanel, SearchInput } from '@/components/books'
import type { BookFilters } from '@/components/books/FilterPanel'
import { Button, ConfirmDialog, Modal, Pagination } from '@/components/ui'
import { useBooksWithFilters, useCreateBook, useDeleteBook, useUpdateBook } from '@/hooks/books'
import { useUpdateBookGenres } from '@/hooks/genres'
import { useDebounce } from '@/hooks/useDebounce'
import { useFilterPersistence } from '@/hooks/useFilterPersistence'
import { captureError } from '@/lib/monitoring'
import type { Book, CreateBook } from '@/types'

export function BooksPage() {
  const { t } = useTranslation()
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingBook, setEditingBook] = useState<Book | null>(null)
  const [bookToDelete, setBookToDelete] = useState<Book | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [error, setError] = useState<string | null>(null)
  const pageSize = 20

  // Load persisted filters
  const { filters, updateFilters, isInitialized } = useFilterPersistence('library')

  // Debounced search to avoid excessive API calls
  const [searchInput, setSearchInput] = useState(filters.search)
  const debouncedSearch = useDebounce(searchInput, 600) // 600ms delay

  // Use new database-level filtering and pagination
  const {
    data: booksData,
    isLoading,
    error: queryError,
  } = useBooksWithFilters({
    filters: {
      ...filters,
      search: debouncedSearch, // Use debounced search instead of direct filters.search
      statuses: filters.statuses.length > 0 ? filters.statuses : ['unread', 'reading', 'read'], // Exclude wishlist by default
      type: 'physical', // Filter only physical books
    },
    page: currentPage,
    pageSize,
    enabled: isInitialized,
  })

  const createBookMutation = useCreateBook()
  const updateBookMutation = useUpdateBook()
  const updateBookGenresMutation = useUpdateBookGenres()
  const deleteBookMutation = useDeleteBook()

  // Reset to page 1 when filters change
  const handleFiltersChange = (newFilters: BookFilters) => {
    // Only update if filters actually changed (deep comparison for key fields)
    const filtersChanged =
      JSON.stringify(filters.genres) !== JSON.stringify(newFilters.genres) ||
      JSON.stringify(filters.statuses) !== JSON.stringify(newFilters.statuses) ||
      filters.ratingRange.min !== newFilters.ratingRange.min ||
      filters.ratingRange.max !== newFilters.ratingRange.max ||
      filters.readYear !== newFilters.readYear ||
      filters.sortBy !== newFilters.sortBy ||
      filters.sortOrder !== newFilters.sortOrder

    if (filtersChanged) {
      updateFilters(newFilters)
      setCurrentPage(1)
    }
  }

  const handleSearchChange = (search: string) => {
    setSearchInput(search)
  }

  // Reset to page 1 when debounced search changes (after user stops typing)
  useEffect(() => {
    if (debouncedSearch !== filters.search) {
      updateFilters({ ...filters, search: debouncedSearch })
      setCurrentPage(1)
    }
  }, [debouncedSearch, filters, updateFilters])

  // Keep searchInput in sync with filters when they change externally
  useEffect(() => {
    setSearchInput(filters.search)
  }, [filters.search])

  const handleAddBook = async (data: CreateBook, genreIds: string[]) => {
    try {
      setError(null)
      const newBook = await createBookMutation.mutateAsync(data)

      // Update genres if any
      if (genreIds.length > 0) {
        await updateBookGenresMutation.mutateAsync({
          bookId: newBook.id,
          genreIds,
        })
      }

      setShowAddForm(false)
    } catch (error) {
      captureError(error as Error, { context: 'Failed to create book' })
      setError(error instanceof Error ? error.message : 'Failed to add book')
    }
  }

  const handleUpdateBook = async (data: CreateBook, genreIds: string[]) => {
    if (!editingBook) return

    try {
      setError(null)

      // Update book data
      await updateBookMutation.mutateAsync({
        bookId: editingBook.id,
        data: {
          title: data.title,
          author: data.author,
          isbn: data.isbn,
          status: data.status,
          rating: data.rating,
          notes: data.notes,
          cover_url: data.cover_url,
          start_date: data.start_date,
          end_date: data.end_date,
          ownership: data.ownership,
          borrowed_from: data.borrowed_from,
        },
      })

      // Update genres
      await updateBookGenresMutation.mutateAsync({
        bookId: editingBook.id,
        genreIds,
      })

      setEditingBook(null)
    } catch (error) {
      captureError(error as Error, { context: 'Failed to update book' })
      setError(error instanceof Error ? error.message : 'Failed to update book')
    }
  }

  const handleEditBook = (book: Book) => {
    setEditingBook(book)
  }

  const handleDeleteBook = (book: Book) => {
    setBookToDelete(book)
  }

  const confirmDeleteBook = async () => {
    if (!bookToDelete) return

    try {
      setError(null)
      await deleteBookMutation.mutateAsync(bookToDelete.id)
      setBookToDelete(null)
    } catch (error) {
      captureError(error as Error, { context: 'Failed to delete book' })
      setError(error instanceof Error ? error.message : 'Failed to delete book')
    }
  }

  const handleFormSubmit = editingBook ? handleUpdateBook : handleAddBook

  const books = booksData?.books || []
  const totalCount = booksData?.totalCount || 0
  const pageCount = booksData?.pageCount || 0
  const displayError = error || queryError

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t('books.myBooks')}</h1>
          <p className="text-muted-foreground mt-1">
            {!isInitialized || isLoading
              ? t('common.loading')
              : totalCount > 0
                ? t('books.booksCount', { count: totalCount })
                : t('books.yourCollection')}
          </p>
        </div>
        <Button
          onClick={() => setShowAddForm(true)}
          className="w-full sm:w-auto"
          disabled={showAddForm}
        >
          {t('books.addBook')}
        </Button>
      </div>

      {/* Error Display */}
      {displayError && (
        <div className="rounded-lg border border-error/20 bg-error/5 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-error" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-error">
                {displayError instanceof Error ? displayError.message : displayError}
              </p>
            </div>
            <div className="ml-auto pl-3">
              <button
                type="button"
                onClick={() => setError(null)}
                className="inline-flex rounded-md bg-error/5 p-1.5 text-error hover:bg-error/10 focus:outline-none focus:ring-2 focus:ring-error focus:ring-offset-2 focus:ring-offset-background"
              >
                <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Book Modal */}
      <Modal
        isOpen={showAddForm || !!editingBook}
        onClose={() => {
          setShowAddForm(false)
          setEditingBook(null)
          setError(null)
        }}
        title={editingBook ? t('books.editBook') : t('books.addBook')}
        size="lg"
      >
        <BookForm
          onSubmit={handleFormSubmit}
          onCancel={() => {
            setShowAddForm(false)
            setEditingBook(null)
            setError(null)
          }}
          isLoading={
            createBookMutation.isPending ||
            updateBookMutation.isPending ||
            updateBookGenresMutation.isPending
          }
          initialData={
            editingBook
              ? {
                  title: editingBook.title,
                  author: editingBook.author,
                  isbn: editingBook.isbn || '',
                  status: editingBook.status,
                  type: editingBook.type || 'physical',
                  rating: editingBook.rating || undefined,
                  notes: editingBook.notes || '',
                  cover_url: editingBook.cover_url || undefined,
                  start_date: editingBook.start_date || undefined,
                  end_date: editingBook.end_date || undefined,
                  ownership: editingBook.ownership || 'owned',
                  borrowed_from: editingBook.borrowed_from || undefined,
                }
              : { type: 'physical' }
          }
          initialGenres={editingBook?.book_genres?.map((bg) => bg.genre.id) || []}
        />
      </Modal>

      {/* Search and Filters */}
      <div className="space-y-4">
        {/* Search Bar and Filter Toggle */}
        <div className="flex gap-3">
          <SearchInput
            value={searchInput}
            onChange={handleSearchChange}
            placeholder={t('search.searchByTitleAuthor')}
            className="flex-1"
          />

          <FilterPanel filters={filters} onFiltersChange={handleFiltersChange} compact={true} />
        </div>
      </div>

      {/* Books List */}
      <div className="mt-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center gap-2 text-muted-foreground">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              {t('common.loading')}
            </div>
          </div>
        ) : books && books.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {books.map((book) => (
                <BookCard
                  key={book.id}
                  book={book}
                  onEdit={handleEditBook}
                  onDelete={handleDeleteBook}
                  context="library"
                />
              ))}
            </div>

            {/* Pagination */}
            {pageCount > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={pageCount}
                onPageChange={setCurrentPage}
                totalItems={totalCount}
                pageSize={pageSize}
                className="mt-8"
              />
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <div className="text-muted-foreground mb-4">
              <svg
                className="mx-auto h-16 w-16"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">
              {filters.search ||
              filters.genres.length > 0 ||
              filters.statuses.length > 0 ||
              filters.ratingRange.min > 0 ||
              filters.ratingRange.max < 5 ||
              !!filters.readYear
                ? t('common.noResultsMessage')
                : t('books.noBooks')}
            </h3>
            <p className="text-muted-foreground mb-6">
              {filters.search ||
              filters.genres.length > 0 ||
              filters.statuses.length > 0 ||
              filters.ratingRange.min > 0 ||
              filters.ratingRange.max < 5 ||
              !!filters.readYear
                ? t('books.tryAdjustingFilters')
                : t('books.startAddingBooks')}
            </p>
            <Button onClick={() => setShowAddForm(true)}>{t('books.addFirstBook')}</Button>
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!bookToDelete}
        title={t('books.confirmDelete.title')}
        message={t('books.confirmDelete.message', { title: bookToDelete?.title })}
        confirmText={t('common.delete')}
        onConfirm={confirmDeleteBook}
        onCancel={() => setBookToDelete(null)}
        variant="danger"
      />
    </div>
  )
}
